// Funciones de calculo y configuracion default para presupuestos comerciales.
// Port directo desde "Presupuesto Habitia.standalone.html" (PRESUPUESTO_CONFIG, fmt, calcular).

/**
 * @typedef {"ejecutivo" | "sello" | "banca"} EstiloPrecio
 * @typedef {{ id: string, tipo: string, cantidad: number, precio: number }} Unidad
 * @typedef {{ id: string, nombre: string, precio: number }} Amenity
 * @typedef {{ id: string, cargo: string, cantidad: number, precio: number }} Empleado
 * @typedef {{ id: string, concepto: string, cantidad: number, precio: number }} Extra
 * @typedef {Object} PresupuestoConfig
 * @property {string} edificio
 * @property {string} ubicacion
 * @property {string} propuestaNro
 * @property {string} fecha
 * @property {string} validez
 * @property {string} contacto
 * @property {string} telefono
 * @property {string} web
 * @property {Unidad[]} unidades
 * @property {Amenity[]} amenities
 * @property {Empleado[]} empleados
 * @property {Extra[]} [extras] — suman al total pero no aparecen como categoría en el documento (ej: ascensores)
 * @property {boolean} aplicaPromo
 * @property {number}  promoPorcentaje
 * @property {number}  promoMeses
 * @property {EstiloPrecio} estiloPrecio
 */

export const fmt = (n) => "$ " + Math.round(n).toLocaleString("es-UY");

export function calcular(c) {
  const subUnidades = c.unidades.reduce(
    (s, u) => s + (u.cantidad || 0) * (u.precio || 0),
    0
  );
  const subAmenities = c.amenities.reduce((s, a) => s + (a.precio || 0), 0);
  const subEmpleados = c.empleados.reduce(
    (s, e) => s + (e.cantidad || 0) * (e.precio || 0),
    0
  );
  // Extras (ej: ascensores): entran al total pero no como categoría visible del documento.
  const subExtras = (c.extras || []).reduce(
    (s, e) => s + (e.cantidad || 0) * (e.precio || 0),
    0
  );
  const totalUnidades = c.unidades.reduce((s, u) => s + (u.cantidad || 0), 0);
  const totalEmpleados = c.empleados.reduce((s, e) => s + (e.cantidad || 0), 0);
  const total = subUnidades + subAmenities + subEmpleados + subExtras;
  const promoTotal = c.aplicaPromo
    ? Math.round(total * (1 - c.promoPorcentaje / 100))
    : total;
  return {
    subUnidades,
    subAmenities,
    subEmpleados,
    subExtras,
    total,
    totalUnidades,
    totalEmpleados,
    promoTotal,
  };
}

// ============================================================================
// Cotizador rapido — tarifa escalonada por tramos marginales
// ============================================================================
//
// La escala funciona como una franja progresiva: cada apartamento paga el precio
// del tramo en el que cae (los primeros 10 a $550, del 11 al 30 a $500, etc.).
// El total crece de forma continua — sin saltos al cruzar de tramo — y el precio
// promedio por apartamento baja gradualmente en edificios grandes, sin tocar la
// tarifa base de los edificios chicos.
//
// Precios en pesos uruguayos FINALES, CON IVA INCLUIDO (decision comercial
// 05-ago-2026: "$550 IVA incluido", no "$550 + IVA"). El documento se arma
// linea a linea con estos mismos precios, asi el total cierra exacto.

export const TARIFA_RAPIDA = {
  tramosApartamentos: [
    { hasta: 10, precio: 550 },
    { hasta: 30, precio: 500 },
    { hasta: 60, precio: 450 },
    { hasta: 100, precio: 400 },
    { hasta: 150, precio: 350 },
    { hasta: Infinity, precio: 300 },
  ],
  // Piso del componente apartamentos: un edificio de 2 o 3 unidades igual paga
  // esto (equivale a 5 apartamentos a tarifa base).
  minimoApartamentos: 2750,
  precioLocal: 500,
  adicionales: {
    portero: 1200,    // liquidacion de sueldo: BPS, aguinaldo, licencia, salario vacacional
    limpiadora: 800,  // idem nomina de limpieza
    amenity: 600,     // gestion de reservas y coordinacion de mantenimiento
    ascensor: 600,    // contrato de mantenimiento, inspecciones, incidencias
    garage: 600,      // gestion de cocheras — mismo precio que ascensor
  },
  // Edificios con mas de `umbralAnios` años pagan el total multiplicado por
  // `factor` (mas incidencias, mas mantenimiento, mas gestion).
  antiguedad: { umbralAnios: 30, factor: 1.1 },
  ivaPorcentaje: 22,
  // El total IVA incluido se redondea HACIA ARRIBA a este multiplo para
  // presentar numeros redondos al cliente ($7.747 -> $7.800).
  redondeoConIva: 100,
};

const num = (v) => Math.max(0, Math.floor(Number(v) || 0));

/**
 * Aplica la escala de tramos marginales a una cantidad de apartamentos.
 * @returns {{ total: number, promedio: number, detalle: Array<{desde:number, hasta:number, cantidad:number, precio:number, subtotal:number}> }}
 */
export function cotizarApartamentos(cantidad, tramos = TARIFA_RAPIDA.tramosApartamentos) {
  const n = num(cantidad);
  const detalle = [];
  let desde = 1;
  let total = 0;
  for (const t of tramos) {
    if (desde > n) break;
    const hasta = Math.min(n, t.hasta);
    const enTramo = hasta - desde + 1;
    detalle.push({ desde, hasta, cantidad: enTramo, precio: t.precio, subtotal: enTramo * t.precio });
    total += enTramo * t.precio;
    desde = hasta + 1;
  }
  return { total, detalle, promedio: n > 0 ? total / n : 0 };
}

/**
 * Cotizacion completa a partir de las cantidades que carga el comercial.
 * Todos los montos son FINALES (IVA incluido) — la tarifa ya lo trae adentro.
 *
 * @param {{ apartamentos?: number, locales?: number, porteros?: number, limpiadoras?: number,
 *           amenities?: number, ascensores?: number,
 *           aplicaPromo?: boolean, promoPorcentaje?: number, promoMeses?: number }} inputs
 */
export function cotizacionRapida(inputs, tarifa = TARIFA_RAPIDA) {
  const cantidades = {
    apartamentos: num(inputs.apartamentos),
    locales: num(inputs.locales),
    porteros: num(inputs.porteros),
    limpiadoras: num(inputs.limpiadoras),
    amenities: num(inputs.amenities),
    ascensores: num(inputs.ascensores),
    garages: num(inputs.garages),
  };
  const antiguedadAnios = num(inputs.antiguedadAnios);
  const { apartamentos, locales, porteros, limpiadoras, amenities, ascensores, garages } = cantidades;

  const escala = cotizarApartamentos(apartamentos, tarifa.tramosApartamentos);
  const subApartamentos = apartamentos > 0 ? Math.max(escala.total, tarifa.minimoApartamentos) : 0;
  const minimoAplicado = apartamentos > 0 && subApartamentos > escala.total;
  const tarifaBase = tarifa.tramosApartamentos[0].precio;
  // Cuanto ahorra el edificio por la escala, contra pagar todo a tarifa base.
  const ahorroVolumen = minimoAplicado ? 0 : apartamentos * tarifaBase - escala.total;

  const conceptos = [];
  if (apartamentos > 0) {
    conceptos.push({
      id: "apartamentos",
      label: minimoAplicado ? "Apartamentos (tarifa minima)" : "Apartamentos",
      cantidad: apartamentos,
      subtotal: subApartamentos,
    });
  }
  if (locales > 0) conceptos.push({ id: "locales", label: "Locales comerciales", cantidad: locales, subtotal: locales * tarifa.precioLocal });
  if (porteros > 0) conceptos.push({ id: "porteros", label: "Portero — liquidacion de sueldo", cantidad: porteros, subtotal: porteros * tarifa.adicionales.portero });
  if (limpiadoras > 0) conceptos.push({ id: "limpiadoras", label: "Limpieza — liquidacion de sueldo", cantidad: limpiadoras, subtotal: limpiadoras * tarifa.adicionales.limpiadora });
  if (amenities > 0) conceptos.push({ id: "amenities", label: "Amenities — gestion y reservas", cantidad: amenities, subtotal: amenities * tarifa.adicionales.amenity });
  if (ascensores > 0) conceptos.push({ id: "ascensores", label: "Ascensores — gestion de mantenimiento", cantidad: ascensores, subtotal: ascensores * tarifa.adicionales.ascensor });
  // Garage: tarifa unica — se cobra una sola vez, sin importar cuantas cocheras.
  if (garages > 0) conceptos.push({ id: "garages", label: "Garage — gestion de cocheras", cantidad: 1, subtotal: tarifa.adicionales.garage });

  // Los precios de la tarifa ya incluyen IVA: el subtotal ES el monto final.
  // Antiguedad: por encima del umbral, el total se multiplica por el factor.
  // Despues se redondea comercialmente hacia arriba al proximo multiplo.
  const subtotal = conceptos.reduce((s, c) => s + c.subtotal, 0);
  const antiguedadAplica = subtotal > 0 && antiguedadAnios > tarifa.antiguedad.umbralAnios;
  const recargoAntiguedad = antiguedadAplica ? Math.round(subtotal * (tarifa.antiguedad.factor - 1)) : 0;
  const paso = tarifa.redondeoConIva || 1;
  const base = subtotal + recargoAntiguedad;
  const redondeo = base > 0 ? (paso - (base % paso)) % paso : 0;
  const totalConIva = base + redondeo;

  const aplicaPromo = !!inputs.aplicaPromo;
  const promoPorcentaje = Number(inputs.promoPorcentaje) || 0;
  const promoMeses = num(inputs.promoMeses);
  const promoTotalConIva = aplicaPromo ? Math.round(totalConIva * (1 - promoPorcentaje / 100)) : totalConIva;

  return {
    cantidades,
    conceptos,
    escala,
    minimoAplicado,
    ahorroVolumen,
    subtotal,
    antiguedadAnios,
    antiguedadAplica,
    recargoAntiguedad,
    redondeo,
    totalConIva,
    // Componente apartamentos por unidad (comparable con la tarifa base de $400).
    porApartamento: apartamentos > 0 ? subApartamentos / apartamentos : 0,
    // Servicio completo dividido por apartamentos (argumento de venta).
    totalPorApartamentoConIva: apartamentos > 0 ? totalConIva / apartamentos : 0,
    aplicaPromo,
    promoPorcentaje,
    promoMeses,
    promoTotalConIva,
  };
}

/**
 * Genera las lineas de unidades / amenities / empleados / extras a partir de
 * las cantidades de la cotizacion rapida. Los precios de la tarifa ya son
 * finales (IVA incluido), van directo a las lineas y el total del template
 * (calcular) coincide exacto con totalConIva de la cotizacion.
 * Lo consumen el Cotizador del comercial y la seccion "Cotizacion rapida"
 * del editor de Presupuestos (admin / superadmin).
 * Los ascensores van como extras: suman al total pero no figuran como
 * categoria (amenity/empleado) en el documento al cliente.
 *
 * @param {Parameters<typeof cotizacionRapida>[0]} inputs
 * @returns {{ unidades: Unidad[], amenities: Amenity[], empleados: Empleado[], extras: Extra[] }}
 */
export function lineasDesdeCotizacion(inputs, tarifa = TARIFA_RAPIDA) {
  const q = cotizacionRapida(inputs, tarifa);
  const { apartamentos, locales, porteros, limpiadoras, amenities, ascensores, garages } = q.cantidades;

  const unidades = [];
  if (q.minimoAplicado) {
    // Caso raro (edificios de menos de 5 apartamentos): una sola linea con el
    // minimo prorrateado. Puede diferir en $1-2 del total por redondeo.
    unidades.push({
      id: "apt-min",
      tipo: "Apartamentos (tarifa minima)",
      cantidad: apartamentos,
      precio: Math.round(Math.max(q.escala.total, tarifa.minimoApartamentos) / apartamentos),
    });
  } else {
    q.escala.detalle.forEach((t, i) => {
      unidades.push({
        id: `apt-${i}`,
        tipo: q.escala.detalle.length > 1 ? `Apartamentos (${t.desde} a ${t.hasta})` : "Apartamentos",
        cantidad: t.cantidad,
        precio: t.precio,
      });
    });
  }
  if (locales > 0) {
    unidades.push({ id: "locales", tipo: "Locales comerciales", cantidad: locales, precio: tarifa.precioLocal });
  }

  const amenityLines = [];
  for (let i = 0; i < amenities; i++) {
    amenityLines.push({ id: `am-${i}`, nombre: amenities > 1 ? `Amenity ${i + 1} (gestion y reservas)` : "Amenity (gestion y reservas)", precio: tarifa.adicionales.amenity });
  }

  const empleados = [];
  if (porteros > 0) empleados.push({ id: "e-portero", cargo: "Portero", cantidad: porteros, precio: tarifa.adicionales.portero });
  if (limpiadoras > 0) empleados.push({ id: "e-limpieza", cargo: "Limpieza", cantidad: limpiadoras, precio: tarifa.adicionales.limpiadora });

  const extras = [];
  if (ascensores > 0) {
    extras.push({
      id: "x-ascensores",
      concepto: ascensores === 1 ? "Ascensor (mantenimiento e inspecciones)" : "Ascensores (mantenimiento e inspecciones)",
      cantidad: ascensores,
      precio: tarifa.adicionales.ascensor,
    });
  }
  // Garage: tarifa unica (una linea, cantidad 1) aunque haya varias cocheras.
  if (garages > 0) {
    extras.push({
      id: "x-garages",
      concepto: "Garage (gestion de cocheras)",
      cantidad: 1,
      precio: tarifa.adicionales.garage,
    });
  }
  // Recargo por antiguedad: linea propia para que el documento cierre exacto.
  if (q.recargoAntiguedad > 0) {
    extras.push({
      id: "x-antiguedad",
      concepto: `Recargo por antigüedad del edificio (+${Math.round((tarifa.antiguedad.factor - 1) * 100)}%)`,
      cantidad: 1,
      precio: q.recargoAntiguedad,
    });
  }
  // La linea de redondeo lleva el total del documento al numero redondo cotizado.
  if (q.redondeo > 0) {
    extras.push({ id: "x-redondeo", concepto: "Ajuste de redondeo", cantidad: 1, precio: q.redondeo });
  }

  return { unidades, amenities: amenityLines, empleados, extras };
}

/**
 * Arma un PresupuestoConfig completo (el que consumen PresupuestoTemplate/Form
 * y el PDF) a partir de la cotizacion rapida.
 *
 * @param {Parameters<typeof cotizacionRapida>[0]} inputs
 * @param {Partial<PresupuestoConfig>} overrides — edificio, ubicacion, propuestaNro, etc.
 * @returns {PresupuestoConfig}
 */
export function configDesdeCotizacion(inputs, overrides = {}, tarifa = TARIFA_RAPIDA) {
  const q = cotizacionRapida(inputs, tarifa);
  return {
    ...defaultConfig(),
    ...overrides,
    ...lineasDesdeCotizacion(inputs, tarifa),
    aplicaPromo: q.aplicaPromo,
    promoPorcentaje: q.promoPorcentaje,
    promoMeses: q.promoMeses,
  };
}

// Numero de propuesta autogenerado: AAAA-NNNN basado en el anio actual + secuencia aleatoria.
function generarPropuestaNro() {
  const year = new Date().getFullYear();
  const seq = String(Math.floor(Math.random() * 9000) + 1000);
  return `${year}-${seq}`;
}

// Fecha legible "DD mmm AAAA" en castellano (ej: "29 abr 2026").
function fechaHoy() {
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const d = new Date();
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Configuracion inicial para "Nuevo presupuesto". Las lineas de ejemplo se
 * derivan de la tarifa oficial (TARIFA_RAPIDA) para que el editor arranque
 * consistente con el cotizador: 24 apartamentos + 2 locales, portero,
 * limpieza y 3 amenities.
 * @returns {PresupuestoConfig}
 */
export function defaultConfig() {
  const lineas = lineasDesdeCotizacion({ apartamentos: 24, locales: 2, porteros: 1, limpiadoras: 1, amenities: 3 });
  const nombresAmenities = ["Piscina", "Gimnasio", "SUM / Salón comunal"];
  return {
    edificio: "Edificio Millán",
    ubicacion: "Av. Millán 4157, Montevideo",
    propuestaNro: generarPropuestaNro(),
    fecha: fechaHoy(),
    validez: "30 días corridos",
    contacto: "info@habitia.ai",
    telefono: "+598 99 496 513",
    web: "habitia.ai",
    unidades: lineas.unidades,
    amenities: lineas.amenities.map((a, i) => ({ ...a, nombre: nombresAmenities[i] || a.nombre })),
    empleados: lineas.empleados,
    extras: lineas.extras,
    aplicaPromo: true,
    promoPorcentaje: 50,
    promoMeses: 3,
    estiloPrecio: "ejecutivo",
    // ── Personalización de la propuesta (opcional; si están, el template
    //    renderiza el estilo "propuesta personalizada" — radiografía + prosa) ──
    ciudad: "Montevideo",          // portada ("Ciudad · fecha")
    jurisdiccion: "Montevideo",    // tribunales competentes (Montevideo, Maldonado, …)
    clienteNombre: "",             // "Cliente" en portada (ej. "Comisión · Edificio X"); default = edificio
    coverLead: "",                 // bajada de portada personalizada (default genérica)
    precioLead: "",                // bajada de la página económica (default genérica)
    radiografia: null,             // [{ n, l }] (celdas de catastro) + notaRadiografia
    notaRadiografia: "",
    personalizado: null,           // [{ titulo, cuerpo }] — reemplaza los servicios genéricos de la pág. 3
  };
}
